import { useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturingClient";
import {
  matchSpokenWalkMaterial,
  parseSpokenWalkLines,
  type SpokenWalkKind,
} from "@/lib/manufacturing/spokenWalkDictation";
import { resolvePackedPieceQty } from "@/lib/manufacturing/spokenWalkPlain";
import { ghostBtnClass, mfgPrimaryBtnClass } from "./manufacturingSurfaceClasses";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";

const COPY: Record<SpokenWalkKind, { title: string; hint: string; placeholder: string }> = {
  packed: {
    title: "Dictate this count",
    hint: "Say it the way you see it. “Two pallets of ProStruct, 50 sheets each.” Match fills the form. Confirm still posts.",
    placeholder: "two pallets of 4x8 flooring, 50 sheets each…",
  },
  waste: {
    title: "Dictate this pile",
    hint: "Say each pile, including a correction, reuse, or waste. Match fills the form. Confirm still posts.",
    placeholder: "one 4x6 runner, slightly bent, reuse it…",
  },
};

export function WalkDictationPad({
  kind,
  materials,
  disabled,
  onApply,
}: {
  kind: SpokenWalkKind;
  materials: MfgMaterial[];
  disabled?: boolean;
  onApply: (row: { material: MfgMaterial; primaryQty: number; secondaryQty: number | null }) => void;
}) {
  const copy = COPY[kind];
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState<
    Array<{
      material: MfgMaterial;
      primaryQty: number;
      secondaryQty: number | null;
      status: string;
      qtyNote: string | null;
    }>
  >([]);
  const [padReset, setPadReset] = useState(0);
  const catalog = useMemo(
    () => materials.map((row) => ({ id: row.id, name: row.name, code: row.code })),
    [materials],
  );

  function handleExtract(transcript: string) {
    const drafts = parseSpokenWalkLines(kind, transcript);
    if (drafts.length === 0) {
      setPending([]);
      setNote(
        kind === "packed"
          ? "Couldn’t find a quantity and a material. Try “two pallets of ProStruct, 50 sheets each.”"
          : "Couldn’t find what you see and a material. Try “one 4x6 runner, slightly bent, reuse it.”",
      );
      return;
    }
    const missed: string[] = [];
    const next: Array<{
      material: MfgMaterial;
      primaryQty: number;
      secondaryQty: number | null;
      status: string;
      qtyNote: string | null;
    }> = [];
    for (const draft of drafts) {
      const match = matchSpokenWalkMaterial(draft, catalog);
      const material = materials.find((row) => row.id === match.candidate?.id) ?? null;
      if (!material || match.status === "unmatched") {
        missed.push(draft.materialName);
        continue;
      }
      const resolved = kind === "packed" ? resolvePackedPieceQty(draft, material) : { qty: draft.primaryQty, note: null };
      next.push({
        material,
        primaryQty: resolved.qty,
        secondaryQty: draft.secondaryQty,
        status: match.status,
        qtyNote: resolved.note,
      });
    }
    setPending(next);
    if (next.length === 0) {
      setNote(`Heard “${missed.join("”, “")}” and nothing in this plant catalog matched.`);
      return;
    }
    const missNote = missed.length > 0 ? ` No catalog match for “${missed.join("”, “")}”.` : "";
    const useBlank = next.some((row) => row.secondaryQty == null) && kind === "waste";
    setNote(
      `${useBlank ? "Use was not heard on a pile. Leave it blank and that pile counts as scrap, or type how many to use." : ""}${missNote}`.trim() ||
        null,
    );
  }

  function fill(index: number) {
    const row = pending[index];
    if (!row) return;
    onApply(row);
    const rest = pending.filter((_, item) => item !== index);
    setPending(rest);
    setNote(rest.length > 0 ? "Filled one. Confirm it, then fill the next pile." : "Filled the form. Confirm when the numbers look right.");
    if (rest.length === 0) setPadReset((current) => current + 1);
  }

  return (
    <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <p className="text-xs font-semibold text-gray-800 dark:text-zinc-100">{copy.title}</p>
      <p className="text-[11px] text-gray-500">{copy.hint}</p>
      <ManufacturingDictatePad
        disabled={disabled}
        placeholder={copy.placeholder}
        extractLabel="Match material"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      {note ? <p className="text-[11px] text-amber-800 dark:text-amber-200">{note}</p> : null}
      {pending.map((row, index) => (
        <div key={`${row.material.id}-${index}`} className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-800 dark:text-zinc-100">
          <p>
            {row.material.code} · {row.material.name} ({row.status}).{" "}
            {kind === "packed" ? "Packed" : "Seen"} {row.primaryQty}
            {row.secondaryQty == null ? "" : `, ${kind === "packed" ? "bays" : "use"} ${row.secondaryQty}`}
            {row.qtyNote ? `. ${row.qtyNote}` : ""}.
          </p>
          <div className="flex gap-2">
            <button type="button" className={ghostBtnClass} disabled={disabled} onClick={() => setPending((current) => current.filter((_, item) => item !== index))}>
              Discard
            </button>
            <button type="button" className={mfgPrimaryBtnClass} disabled={disabled} onClick={() => fill(index)}>
              Fill form
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
